import moment from "moment";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@common/reducers/hooks/store.hook";
import { setIsRecurring, setRruleset, setDueDate } from "@common/reducers/ScheduledMessageEditorReducer";
import { getUserStartDayOfWeek, selectCurrentWorkspace } from "@common/reducers/UserSelectors";
import { Frequency, Options, RRule, Weekday } from "rrule";
import {
    extractWeekdayNumbers,
    getRuleOptionsSafe,
    WeekStartDay,
    resolveRecurrenceCommit,
    ruleTimeBaseFor,
    stampRuleTimeBase,
    endOfDayInZone,
    resolveStampingTzid,
} from "./recurrence-utils";
import { cadenceDropWarning } from './recurrence-cadence-warning';
import type { StartMoveReason } from './recurrence-commit-plan';
import { useNowTicker } from '@extension/context/content/components/add-scheduled-message-button/use-now-ticker';
import { convertUIToRRuleWeekdays, getSortedWeekdayButtons } from "../../../bt-common/utils/weekday-conversion";
import { cn } from "@/lib/utils";
import { useTranslation } from 'react-i18next';
import { formatInLanguage } from '@common/config/moment-locales';
import { namespace } from '@common/config/constants';

// The notice's two sentences, one per reason the start moved. Held as consts
// ending in `Key` so the translation-key guard can read them statically —
// `t(cond ? 'a' : 'b')` is the one shape it cannot follow.
const startWillMoveKey = 'recurrenceOptions.startWillMove';
const startWillMovePassedKey = 'recurrenceOptions.startWillMovePassed';
const startMoveNoticeKey: Record<StartMoveReason, string> = {
    'off-pattern': startWillMoveKey,
    passed: startWillMovePassedKey,
};

const cadenceDropYearKey = 'recurrenceOptions.cadenceDropYear';
const cadenceDropMonthKey = 'recurrenceOptions.cadenceDropMonth';

interface RecurrenceOptionsPopoverProps {
    onClose: () => void;
    /** FE#240: restrict the frequency dropdown (default: all six). Campaigns pass
     *  [DAILY, WEEKLY, MONTHLY, YEARLY] — sub-daily bulk recurrence is nonsensical. */
    allowedFrequencies?: Frequency[];
}

const RecurrenceOptionsPopover = ({
    onClose,
    allowedFrequencies,
}: RecurrenceOptionsPopoverProps) => {
    const freqAllowed = (f: Frequency) => !allowedFrequencies || allowedFrequencies.includes(f);
    const { t, i18n } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const startDayAtWeek = useAppSelector(getUserStartDayOfWeek);

    // Read state directly from Redux
    const dueDate = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.dueDate);
    const rruleset = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.rruleset);

    // Local rruleset draft; initialized from current redux state
    const [localRruleset, setLocalRruleset] = useState<string | undefined>(rruleset);

    // FE#5.9.28: an EXISTING rule's OWN embedded TZID always wins (a viewer's zone must
    // not re-zone or drop it); a brand-new rule with no TZID yet is seeded from the
    // VIEWER's browser zone first (→ workspace tz → legacy). This is both the stamping
    // zone AND the zone the default weekly BYDAY / day-of-month default is derived in.
    const workspaceTimezone = useAppSelector(selectCurrentWorkspace)?.timezone;
    const effectiveTz = resolveStampingTzid(rruleset, workspaceTimezone);

    const ruleOptions = useMemo(() => {
        return getRuleOptionsSafe(localRruleset ?? rruleset, dueDate, effectiveTz);
    }, [localRruleset, rruleset, dueDate, effectiveTz]);

    const ends = useMemo(() => {
        if (ruleOptions.until) {
            return "on";
        } else if (ruleOptions.count !== undefined) {
            return "after";
        } else {
            return "never";
        }
    }, [ruleOptions.count, ruleOptions.until]);

    const [occurrencesValue, setOccurrencesValue] = useState(ruleOptions.count?.toString() || "1");

    let momentJSDueDate = moment.utc(dueDate);

    // BE#648 + FE#5.9.28: a series born from this popover carries an IANA zone and its
    // LOCAL wall-clock (DTSTART;TZID + wall-clock BYHOUR), so the shared engine holds
    // the customer's wall-clock across DST. The zone is `effectiveTz` (the rule's own
    // TZID on an edit, else the viewer's browser zone → workspace tz → legacy; computed
    // above). `momentJSDueDate` deliberately stays a true-instant UTC moment — the
    // commit/validity machinery below reasons in true instants — while only the rrule
    // that gets WRITTEN is zone-stamped. No zone -> ruleTimeBaseFor falls back to the
    // legacy UTC (no-TZID) write, so nothing regresses.
    const ruleTimeBase = useMemo(
        () => ruleTimeBaseFor(dueDate, effectiveTz),
        [dueDate, effectiveTz],
    );

    const updateInternalRuleOptions = useCallback((options: Partial<Options>) => {
        const rrule = new RRule(stampRuleTimeBase(options, ruleTimeBase));
        setLocalRruleset(rrule.toString());
    }, [ruleTimeBase]);

    const handleIntervalChange = (event: React.ChangeEvent<any>) => {
        updateInternalRuleOptions({
            ...ruleOptions,
            interval: event.target.value as number,
        });
    };

    const handleFrequencyChange = (event: React.ChangeEvent<any>) => {
        const freq = event.target.value as Frequency;
        const currentDate = new Date();

        // Default options to clear other frequency-specific attributes
        const defaultOptions: Partial<Options> = {
            bysetpos: null,
            bymonth: null,
            bymonthday: null,
            byyearday: null,
            byweekno: null,
            byweekday: null,
            byhour: null,
            byminute: null,
            bysecond: 0
        };

        let newOptions: Partial<Options>;
        switch (freq) {
            case Frequency.YEARLY:
            case Frequency.MONTHLY:
                newOptions = {
                    ...defaultOptions,
                    freq,
                    byhour: ruleTimeBase.byhour,
                    byminute: ruleTimeBase.byminute,
                };
                break;
            case Frequency.WEEKLY:
                newOptions = {
                    ...defaultOptions,
                    byweekday: (() => {
                        const sundayFirst: Weekday[] = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];
                        const mondayFirst: Weekday[] = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU];
                        return (startDayAtWeek === 0 ? sundayFirst : mondayFirst)[currentDate.getDay()].weekday;
                    })(),
                    freq,
                    byhour: ruleTimeBase.byhour,
                    byminute: ruleTimeBase.byminute,
                };
                break;

            case Frequency.DAILY:
            case Frequency.HOURLY:
            case Frequency.MINUTELY:
                newOptions = { ...defaultOptions, interval: ruleOptions.interval || 1, freq };
                break;

            default:
                newOptions = { ...defaultOptions, ...ruleOptions };
                break;
        }

        updateInternalRuleOptions(newOptions);
    };

    const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>(() => 
        extractWeekdayNumbers(ruleOptions.byweekday, startDayAtWeek as WeekStartDay)
    );

    const handleByweekdayChange = (value: number) => {
        // Toggle the weekday
        const newSelected = selectedWeekdays.includes(value)
            ? selectedWeekdays.filter(d => d !== value)
            : [...selectedWeekdays, value];
        
        // Prevent turning off the last day - require at least one day selected
        if (newSelected.length === 0) {
            return; // Don't update if trying to clear all days
        }
        
        setSelectedWeekdays(newSelected);
        const updatedWeekdays = convertUIToRRuleWeekdays(newSelected, startDayAtWeek as WeekStartDay);

        updateInternalRuleOptions({
            ...ruleOptions,
            byweekday: updatedWeekdays,
        });
    };

    // Update selected weekdays when ruleOptions.byweekday changes
    useEffect(() => {
        setSelectedWeekdays(extractWeekdayNumbers(ruleOptions.byweekday, startDayAtWeek as WeekStartDay));
    }, [ruleOptions.byweekday, startDayAtWeek]);

    // WHAT DONE WILL DO, computed BEFORE it is pressed.
    //
    // ONE call, to the same function the tests drive. The popover used to decide
    // validity and the move itself, and asked for a better start only when the
    // chosen one did NOT fit the pattern — the fit check gated the search. A past start that
    // DID fit — every daily start fits — was therefore committed in the past, and
    // the fix already sitting in `findClosestValidOccurrence` (726845379) was never
    // reached from here. `resolveRecurrenceCommit` owns that decision now; this
    // component only reads it, for the notice below AND for the write in Done, so
    // the sentence on screen and the thing committed cannot disagree.
    //
    // On a ticking clock: a popover left open while its start slides into the
    // past must say so before Done, not discover it after.
    //
    // A note on the wording: the start it lands on can be EARLIER than the one the
    // customer chose while still being ahead (a Saturday on a Wednesdays pattern
    // may belong on the Wednesday before). It is therefore never "the next date";
    // "the nearest date that fits" is what it actually is.
    const now = useNowTicker();
    const commitPlan = useMemo(() => {
        try {
            const finalRruleString = localRruleset ?? new RRule(stampRuleTimeBase(ruleOptions, ruleTimeBase)).toString();

            return resolveRecurrenceCommit({
                chosenStart: momentJSDueDate,
                rruleset: finalRruleString,
                dueDate,
                now: moment.utc(now),
            });
        } catch {
            // A rule we cannot even build is not a rule we can describe. Say
            // nothing and let Done fall through to the same failure it always had.
            return null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localRruleset, ruleOptions, dueDate, momentJSDueDate.valueOf(), now, ruleTimeBase]);

    // The moved-to start, in the reader's own language and in LOCAL time. The
    // popover works in `moment.utc`, and printing a UTC date to someone whose
    // evening is the next day in UTC would name a date they never see anywhere
    // else in the product. The time is added only when the move stays on the
    // same calendar day — otherwise two identical dates would read as "no move".
    const movedStart = commitPlan?.startMoved && commitPlan.dueDate ? moment(commitPlan.dueDate).local() : null;
    const movedStartLabel = movedStart
        ? formatInLanguage(
            movedStart,
            i18n.language,
            movedStart.isSame(momentJSDueDate.clone().local(), 'day') ? 'dddd, LL LT' : 'dddd, LL',
        )
        : '';

    // FE#470: the rule the series had when the popover opened. Held in a ref-like
    // state so later edits to the draft never move the baseline.
    const [originalRruleset] = useState<string | undefined>(rruleset);
    const cadenceWarning = useMemo(
        () => cadenceDropWarning({
            originalRrule: originalRruleset,
            draftRrule: localRruleset,
            after: momentJSDueDate.toDate(),
            tzid: effectiveTz,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [originalRruleset, localRruleset, momentJSDueDate.valueOf(), effectiveTz],
    );
    const cadenceWarningDate = cadenceWarning
        ? formatInLanguage(moment(cadenceWarning.next).local(), i18n.language, 'dddd, LL LT')
        : '';

    const handleDone = useCallback(() => {
        try {
            const plan = commitPlan;
            if (plan) {
                if (plan.dueDate) dispatch(setDueDate(plan.dueDate));
                dispatch(setIsRecurring(plan.setRecurring));
                dispatch(setRruleset(plan.rruleset));
            }
        } finally {
            onClose();
        }
    }, [commitPlan, onClose, dispatch]);

    const handleCancel = useCallback(() => {
        onClose();
    }, [onClose]);

    useEffect(() => {
        setOccurrencesValue(ruleOptions.count?.toString() || "1");
    }, [ruleOptions.count]);

    // Initialize local rruleset when popover opens with no existing rruleset
    useEffect(() => {
        if (!localRruleset && ruleOptions.freq === Frequency.WEEKLY && ruleOptions.byweekday) {
            updateInternalRuleOptions(ruleOptions);
        }
    }, [localRruleset, ruleOptions.freq, ruleOptions.byweekday, updateInternalRuleOptions]);

    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const selectedDate = useMemo(() => {
        if (ends === "on" && ruleOptions.until) {
            return new Date(ruleOptions.until);
        }
        return moment().add(1, 'day').toDate();
    }, [ends, ruleOptions.until]);

    return (
        <div className="rounded-2xl p-6 w-[360px] shadow-[0_8px_32px_var(--modal-shadow)] border border-[var(--tasks-cell-border)] flex flex-col gap-6">
            {/* Header */}
            <div className="text-lg font-semibold text-[var(--tasks-text-secondary)] text-left">
                {t('recurrenceOptions.customRecurrence')}
            </div>

            {/* Repeat Every Section */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-row items-center gap-2 min-w-0 flex-nowrap">
                    <span className="text-sm whitespace-nowrap shrink-0 text-[var(--tasks-text-secondary)] font-medium">
                        {t('recurrenceOptions.repeatEvery')}
                    </span>

                    <Input
                        type="number"
                        min={1}
                        value={ruleOptions.interval?.toString() || "1"}
                        onChange={(e) => handleIntervalChange({ target: { value: e.target.value } } as any)}
                        className="w-[60px] shrink-0 text-sm rounded-lg bg-transparent text-center px-3 py-2"
                    />

                    <Select
                        value={ruleOptions.freq?.toString()}
                        onValueChange={(value) => handleFrequencyChange({ target: { value: Number(value) } } as any)}
                    >
                        <SelectTrigger className="w-[110px] shrink-0 text-sm bg-transparent rounded-lg">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent
                            zIndex={95000}
                            side="bottom"
                            sideOffset={6}
                            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] shadow-xl"
                            style={{
                                boxShadow: '0px 16px 48px var(--modal-shadow, rgba(15, 23, 42, 0.16))',
                            }}
                        >
                            {freqAllowed(Frequency.MINUTELY) && <SelectItem value={Frequency.MINUTELY.toString()}>{t('recurrenceOptions.minute')}</SelectItem>}
                            {freqAllowed(Frequency.HOURLY) && <SelectItem value={Frequency.HOURLY.toString()}>{t('recurrenceOptions.hour')}</SelectItem>}
                            {freqAllowed(Frequency.DAILY) && <SelectItem value={Frequency.DAILY.toString()}>{t('recurrenceOptions.day')}</SelectItem>}
                            {freqAllowed(Frequency.WEEKLY) && <SelectItem value={Frequency.WEEKLY.toString()}>{t('recurrenceOptions.week')}</SelectItem>}
                            {freqAllowed(Frequency.MONTHLY) && <SelectItem value={Frequency.MONTHLY.toString()}>{t('recurrenceOptions.month')}</SelectItem>}
                            {freqAllowed(Frequency.YEARLY) && <SelectItem value={Frequency.YEARLY.toString()}>{t('recurrenceOptions.year')}</SelectItem>}
                        </SelectContent>
                    </Select>
                </div>

                {/* Weekday Selection - only show for weekly frequency */}
                {ruleOptions.freq === Frequency.WEEKLY && (
                    <div className="flex flex-col gap-3">
                        <div className="text-sm text-[var(--tasks-text-secondary)] font-medium">
                            {t('recurrenceOptions.repeatOn')}
                        </div>
                        <div className="flex gap-2">
                            {getSortedWeekdayButtons(startDayAtWeek as WeekStartDay).map(({ label, value }) => (
                                <Button
                                    key={value}
                                    variant={selectedWeekdays.includes(value) ? "default" : "outline"}
                                    size="icon"
                                    onClick={() => handleByweekdayChange(value)}
                                    className={cn(
                                        "w-8 h-8 min-w-8 p-0 rounded-full text-xs font-semibold transition-all duration-200",
                                        selectedWeekdays.includes(value)
                                            ? "bg-[var(--color-primary)] text-[var(--color-primary-contrast)] border-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:opacity-90"
                                            : "text-[var(--tasks-text-secondary)] bg-[var(--color-surface)] border border-[var(--tasks-cell-border)] hover:bg-[var(--tasks-menu-hover-bg)] hover:border-[var(--color-accent)]"
                                    )}
                                >
                                    {label.toUpperCase()}
                                </Button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Ends Section */}
            <div className="flex flex-col gap-4">
                <Label className="text-sm text-[var(--tasks-text-secondary)] font-medium mb-2">
                    {t('recurrenceOptions.ends')}
                </Label>
                <RadioGroup
                    value={ends}
                    onValueChange={(v: "never" | "on" | "after") => {
                        const tomorrow = moment.utc().add(1, 'day').toDate();
                        switch (v) {
                            case "never": {
                                updateInternalRuleOptions({
                                    ...ruleOptions,
                                    until: undefined,
                                    count: undefined,
                                })
                                break;
                            }
                            case "on": {
                                updateInternalRuleOptions({
                                    ...ruleOptions,
                                    until: tomorrow,
                                    count: undefined,
                                })
                                break;
                            }
                            case "after": {
                                updateInternalRuleOptions({
                                    ...ruleOptions,
                                    until: undefined,
                                    count: 1,
                                })
                                break;
                            }
                        }
                    }}
                    className="space-y-2"
                >
                    <div className="flex items-start gap-3 mb-2">
                        <div className="flex items-center space-x-2">
                            <RadioGroupItem value="never" id="never" />
                            <Label htmlFor="never" className="text-sm text-[var(--tasks-text-secondary)] cursor-pointer">
                                {t('recurrenceOptions.never')}
                            </Label>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center space-x-2">
                            <RadioGroupItem value="on" id="on" />
                            <Label htmlFor="on" className="text-sm text-[var(--tasks-text-secondary)] cursor-pointer">
                                {t('recurrenceOptions.on')}
                            </Label>
                        </div>
                        {ends === "on" ? (
                            <div className="relative">
                                <Button
                                    variant="outline"
                                    className="h-9 px-3 text-sm rounded-lg bg-transparent"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setDatePickerOpen(!datePickerOpen);
                                    }}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {moment.utc(selectedDate).format("MMM D, YYYY")}
                                </Button>
                                {datePickerOpen && (
                                    <>
                                        <div 
                                            className="fixed inset-0" 
                                            style={{ zIndex: 99998 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDatePickerOpen(false);
                                            }}
                                        />
                                        <div
                                            className="absolute w-auto p-4 bg-[var(--color-surface)]"
                                            style={{
                                                boxShadow: '0 8px 32px var(--modal-shadow)',
                                                borderRadius: '12px',
                                                border: '1px solid var(--tasks-cell-border)',
                                                zIndex: 99999,
                                                top: 'calc(100% + 4px)',
                                                left: 0,
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                <Calendar
                                    mode="single"
                                    selected={selectedDate}
                                    onSelect={(date) => {
                                        if (date) {
                                            // BE#648 (M2): the "until <day>" end is a true UTC instant equal
                                            // to end-of-day in the series' WORKSPACE zone (what the engine
                                            // reads), not the browser zone. No workspace zone -> legacy
                                            // browser-local end-of-day.
                                            const normalizedDate = endOfDayInZone(date, effectiveTz);
                                            updateInternalRuleOptions({
                                                ...ruleOptions,
                                                until: normalizedDate,
                                            });
                                            setDatePickerOpen(false);
                                        }
                                    }}
                                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                                    initialFocus
                                    weekStartsOn={startDayAtWeek === 0 ? 0 : 1}
                                    className="bg-transparent border-0"
                                    style={{
                                        backgroundColor: "transparent",
                                        '--rdp-selected-color': 'var(--tasks-board-bg)',
                                    } as React.CSSProperties}
                                    styles={{
                                        months: {
                                            display: 'flex',
                                            flexDirection: 'column'
                                        },
                                        month: {
                                            display: 'flex',
                                            flexDirection: 'column'
                                        },
                                        caption: {
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            position: 'relative',
                                            paddingBottom: '8px'
                                        },
                                        caption_label: {
                                            fontSize: '16px',
                                            fontWeight: '500'
                                        },
                                        nav: {
                                            display: 'flex',
                                            alignItems: 'center'
                                        },
                                        nav_button: {
                                            height: '28px',
                                            width: '28px',
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            padding: '0',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'var(--tasks-text-secondary)',
                                            boxShadow: 'none',
                                            outline: 'none'
                                        },
                                        nav_button_previous: {
                                            position: 'absolute',
                                            left: '0',
                                            backgroundColor: 'transparent',
                                            border: 'none'
                                        },
                                        nav_button_next: {
                                            position: 'absolute',
                                            right: '0',
                                            backgroundColor: 'transparent',
                                            border: 'none'
                                        },
                                        table: {
                                            width: '100%',
                                            borderCollapse: 'collapse'
                                        },
                                        head_row: {
                                            display: 'flex',
                                            marginBottom: '4px',
                                            width: '100%'
                                        },
                                        head_cell: {
                                            color: 'var(--tasks-text-secondary)',
                                            width: '40px',
                                            height: '32px',
                                            fontWeight: '500',
                                            fontSize: '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            textAlign: 'center'
                                        },
                                        row: {
                                            display: 'flex',
                                            width: '100%'
                                        },
                                        cell: {
                                            position: 'relative',
                                            padding: '0',
                                            textAlign: 'center',
                                            fontSize: '14px',
                                            width: '40px',
                                            height: '40px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        },
                                        day: {
                                            width: '40px',
                                            height: '40px',
                                            padding: '0',
                                            fontWeight: 'normal',
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            boxShadow: 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            borderRadius: '0',
                                            textAlign: 'center',
                                            outline: 'none',
                                            userSelect: 'none',
                                            transition: 'background-color 0.2s'
                                        }
                                    }}
                                    modifiersStyles={{
                                        selected: {
                                            backgroundColor: 'var(--color-primary)',
                                            color: 'var(--tasks-board-bg)',
                                            borderRadius: '50%',
                                            cursor: 'pointer',
                                            fontWeight: '600'
                                        },
                                        today: {
                                            fontWeight: '600',
                                            cursor: 'pointer'
                                        },
                                        outside: {
                                            color: 'var(--calendar-outside-day)',
                                            cursor: 'pointer'
                                        },
                                        disabled: {
                                            color: 'var(--color-text-disabled, hsl(215, 16%, 80%))',
                                            cursor: 'not-allowed',
                                            backgroundColor: 'transparent',
                                            opacity: 0.5
                                        },
                                        hidden: {
                                            visibility: 'hidden'
                                        }
                                    }}
                                />
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : (
                            <Button
                                variant="outline"
                                className="h-9 px-3 text-sm rounded-lg bg-transparent opacity-50 cursor-not-allowed"
                                disabled
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {moment.utc(selectedDate).format("MMM D, YYYY")}
                            </Button>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex items-center space-x-2">
                            <RadioGroupItem value="after" id="after" />
                            <Label htmlFor="after" className="text-sm text-[var(--tasks-text-secondary)] cursor-pointer">
                                {t('recurrenceOptions.after')}
                            </Label>
                        </div>
                        <Input
                            type="number"
                            min={1}
                            disabled={ends !== "after"}
                            value={occurrencesValue}
                            onChange={(e) => {
                                setOccurrencesValue(e.target.value);
                            }}
                            onBlur={() => {
                                const newValue = occurrencesValue === "" ? 1 : +occurrencesValue;
                                updateInternalRuleOptions({
                                    ...ruleOptions,
                                    count: newValue,
                                });
                            }}
                            className={cn(
                                "w-[60px] text-sm rounded-lg bg-transparent text-center px-3 py-2",
                                ends !== "after" && "opacity-50 cursor-not-allowed"
                            )}
                        />
                        <span
                            className={cn(
                                "text-sm",
                                ends !== "after" ? "text-inherit" : "text-[var(--tasks-text-secondary)]"
                            )}
                        >
                            {t('recurrenceOptions.occurrences')}
                        </span>
                    </div>
                </RadioGroup>
            </div>

            {/* THE RE-DATING, SAID BEFORE IT HAPPENS.
                Two reasons, two sentences: the start is not on the pattern
                (Wednesdays picked while the date sits on a Thursday), or the start
                has already passed — which moves it even when it fits. Deliberately
                NOT phrased as "the next date" in either: the search picks by
                absolute distance and can legitimately move BACKWARDS while staying
                ahead of the clock. */}
            {movedStartLabel && commitPlan?.moveReason && (
                <div
                    role="status"
                    className="flex items-start gap-2 rounded-lg border border-[var(--tasks-cell-border)] bg-[var(--tasks-menu-hover-bg)] px-3 py-2 text-sm text-[var(--tasks-text-secondary)]"
                >
                    <CalendarIcon className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                        {t(startMoveNoticeKey[commitPlan.moveReason], { date: movedStartLabel })}
                    </span>
                </div>
            )}

            {/* FE#470: Day/Week -> Month/Year drops the cadence; say so, with the
                date of the next send, before Done. */}
            {cadenceWarning && (
                <div
                    role="status"
                    className="flex items-start gap-2 rounded-lg border border-[var(--tasks-cell-border)] bg-[var(--tasks-menu-hover-bg)] px-3 py-2 text-sm text-[var(--tasks-text-secondary)]"
                >
                    <CalendarIcon className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                        {t(cadenceWarning.unit === 'year' ? cadenceDropYearKey : cadenceDropMonthKey, { date: cadenceWarningDate })}
                    </span>
                </div>
            )}

            {/* Footer with Cancel and Done buttons */}
            <div className="flex flex-row justify-end gap-2 w-full pt-2">
                <Button
                    variant="outline"
                    onClick={handleCancel}
                    className="px-6 py-1.5 rounded-lg border border-[var(--tasks-cell-border)] bg-[var(--color-surface)] text-[var(--tasks-text-secondary)] font-medium text-sm min-w-[80px] transition-all duration-200 hover:bg-[var(--tasks-menu-hover-bg)] hover:border-[var(--color-text)] hover:text-[var(--color-text)]"
                >
                    {t('recurrenceOptions.cancel')}
                </Button>

                <Button
                    onClick={handleDone}
                    className="px-6 py-1.5 rounded-lg bg-[var(--color-primary)] text-[var(--color-primary-contrast)] font-semibold text-sm min-w-[80px] transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_var(--modal-shadow)] active:translate-y-0"
                >
                    {t('recurrenceOptions.done')}
                </Button>
            </div>
        </div>
    )
}

export default RecurrenceOptionsPopover